import { selectIsSuperAdmin, useEffectiveUser } from '../stores/authStore';
import type { ControlledResource, Intent } from '../types/profiles';

/**
 * Fallback permission matrix keyed by role, used when the user record doesn't
 * yet carry a fully-loaded `profile` relationship (e.g. stale persisted state
 * right after login). Mirrors the backend DEFAULT_PROFILES matrix.
 */
const ROLE_FALLBACK: Record<string, Record<ControlledResource, [boolean, boolean, boolean]>> = {
  super_admin: {
    domains: [true, true, true],
    usecases: [true, true, true],
    cadrages: [true, true, true],
    forges: [true, true, true],
    applications: [true, true, true],
    conversations: [true, true, true],
    chatbots: [true, true, true],
    'knowledge-base': [true, true, true],
    evaluations: [true, true, true],
    configuration: [true, true, true],
    'my-organization': [true, true, true],
    'token-consumption': [true, true, true],
    'genie-artifact': [true, true, true],
    cowork: [true, true, true],
    projects: [true, true, true],
    gallery: [true, false, false],
    portfolio: [true, true, false],
  },
  admin: {
    domains: [true, true, true],
    usecases: [true, true, true],
    cadrages: [true, true, true],
    forges: [true, true, true],
    applications: [true, true, true],
    conversations: [true, true, true],
    chatbots: [true, true, true],
    'knowledge-base': [true, true, true],
    evaluations: [true, true, true],
    configuration: [false, false, false],
    'my-organization': [true, true, true],
    'token-consumption': [true, true, true],
    'genie-artifact': [true, true, true],
    cowork: [true, true, true],
    projects: [true, true, true],
    gallery: [true, false, false],
    portfolio: [true, true, false],
  },
  user: {
    domains: [true, true, true],
    usecases: [true, true, true],
    cadrages: [true, true, true],
    forges: [false, false, false],
    applications: [false, false, false],
    conversations: [true, true, true],
    chatbots: [true, true, true],
    'knowledge-base': [true, true, true],
    evaluations: [true, true, true],
    configuration: [false, false, false],
    'my-organization': [true, true, true],
    'token-consumption': [false, false, false],
    'genie-artifact': [true, true, true],
    cowork: [true, true, true],
    projects: [true, true, true],
    gallery: [true, false, false],
    portfolio: [true, false, false],
  },
  // Demo role: same access as a regular user — limitations come from org quota and profile.
  demo: {
    domains: [true, true, true],
    usecases: [true, true, true],
    cadrages: [true, true, true],
    forges: [false, false, false],
    applications: [false, false, false],
    conversations: [true, true, true],
    chatbots: [true, true, true],
    'knowledge-base': [true, true, true],
    evaluations: [true, false, false],
    configuration: [false, false, false],
    'my-organization': [true, false, false],
    'token-consumption': [false, false, false],
    'genie-artifact': [true, true, true],
    cowork: [true, false, false],
    projects: [true, true, true],
    gallery: [true, false, false],
    portfolio: [true, false, false],
  },
};

const INTENT_INDEX: Record<Intent, 0 | 1 | 2> = { read: 0, write: 1, delete: 2 };

/**
 * Hook resolving feature-level permissions for the effective user (impersonated
 * user if impersonating, otherwise the authenticated user).
 *
 * Super-admins bypass all checks.
 */
export function usePermissions() {
  const user = useEffectiveUser();
  // Base the bypass on the EFFECTIVE user, not the real authenticated one: when a
  // super-admin impersonates a normal user, permission checks must reflect the
  // impersonated user's profile (otherwise "Login as" would show full access and
  // be useless for verifying a profile).
  const isSuperAdmin = selectIsSuperAdmin(user);

  const can = (resource: ControlledResource, intent: Intent = 'read'): boolean => {
    if (isSuperAdmin) return true;

    // A loaded profile is authoritative (mirrors the backend): a resource with
    // no matching permission row means no grant — NOT role-default access.
    // Otherwise a profile that simply omits a resource (e.g. one added after the
    // profile was created and never backfilled) would incorrectly appear granted.
    const profile = user?.profile;
    if (profile) {
      const perm = profile.permissions?.find((p) => p.resource === resource);
      return perm ? Boolean(perm[intent]) : false;
    }

    // Fallback only when no profile is attached yet → use the role-default matrix
    // so the UI isn't blank while `/users/me` is in flight or persisted state is stale.
    const role = (user?.role ?? 'user') as string;
    const matrix = ROLE_FALLBACK[role] ?? ROLE_FALLBACK.user;
    const row = matrix[resource];
    return row ? row[INTENT_INDEX[intent]] : false;
  };

  return {
    can,
    canRead: (r: ControlledResource) => can(r, 'read'),
    canWrite: (r: ControlledResource) => can(r, 'write'),
    canDelete: (r: ControlledResource) => can(r, 'delete'),
    profile: user?.profile ?? null,
  };
}
